import { DeviceType } from '@services/constants'
import {
  Device,
  IStatisticalByDeviceType,
  sumStatisticalCountByDeviceType,
} from '@services/device'
import { ISession, useAllSessions } from '@services/session'
import InnmaxMap2, { InnmaxMarker } from '@shared/InnmaxMap2'
import useWatchMap from '@shared/InnmaxMap2/hooks/useWatchMap'
import { ISessionMarkerProps, MapType } from '@shared/InnmaxMap2/map.types'
import { useSetPageHeaderContent } from '@shared/innmaxLib/hooks/usePageHeader'
import useVisible from '@shared/innmaxLib/hooks/useVisible'
import { Layout, Loading } from '@shared/innmaxUI'
import { useLayoutTree } from '@shared/innmaxUI/LayoutTree'
import VideoPlayer from '@shared/innmaxUI/VideoPlayer'
import theme from '@theme'
import { flatten, isEmpty, isNil, path, pathOr, pluck } from 'ramda'
import React from 'react'
import { useHistory } from 'react-router-dom'
import Breadcrumb from 'src/components/Breadcrumb'
import styled, { css, keyframes } from 'styled-components'
import { useUrlSeach } from '../../MapDashboard/useUrlSearch'
import ParkingLotsView from './ParkingLotsView'
import { MapDashboardProvider, useDashboardContext } from './Provider'
import AlarmList from './components/AlarmList'
import ChargingPointSidebar from './components/ChargingPointSidebar'
import Controllers from './components/Controllers'
import FiltersSidebar from './components/FilterSidebar'
import LayerSidebar from './components/LayerSidebar'
import MapSettingDropdown from './components/MapSettingDropdown'
import ParkingLotSidebar from './components/ParkingLotSidebar'
import SimpleDeviceBoard from './components/SimpleDeviceBoard'

const InternalMapDashboard = () => {
  const {
    visible: filterSideBarVisible,
    open: filterSideBarOpen,
    close: filterSideBarClose,
  } = useVisible()
  const {
    visible: layerSidebarVisible,
    open: layerSidebarOpen,
    close: layerSidebarClose,
  } = useVisible()

  const [layoutTree] = useLayoutTree()

  const [isStreetView, setIsStreetView] = React.useState(false)

  const {
    currentSession,
    sessionDevices,
    setCurrentSession,
    sessionsById,
    mapInstance,
    videoPlayer,
    closeVideoPlayer,
    isFilterActived,
    lastUpdated,
    filterVariables,
    loadingDevices,
    deviceCountInfoBySessionId,
    isLoadDeviceCountInfo,
    getSessionDeepStatistical,
    baseLayer,
    parkingLotViewLoading,
    viewMode,
  } = useDashboardContext()

  const history = useHistory()

  useUrlSeach({
    searchDeviceOnSuccess: (session: ISession, device: Device) => {
      setCurrentSession(session)
      mapInstance?.setFocusDevice(device)

      history.replace(history.location.pathname)
    },
    searchDevicesOnSuccess: (data: Device[]) => {
      const _s: ISession[] = data.map(i => sessionsById[i.sessionId])
      if (_s && Array.isArray(_s)) {
        setCurrentSession(_s[0])
        mapInstance?.setSelectedDevices(data)
      }
      history.replace(history.location.pathname)
    },
    searchSessionOnSuccess: (session: ISession) => {
      setTimeout(() => {
        setCurrentSession(session)
      }, 0)
      history.replace(history.location.pathname)
    },
  })

  const closeMarkerSidebar = React.useCallback(() => {
    mapInstance?.setFocusDevice(undefined)
    mapInstance?.setSelectedDevices([])
  }, []) // eslint-disable-line

  const checkIsTreeAndOpenLayerSidebar = React.useCallback(
    (id: number) => {
      const session = sessionsById[id]
      if (!session || session.type !== MapType.TREE) {
        return false
      }
      closeMarkerSidebar()
      layerSidebarOpen()
      setTimeout(
        () =>
          layoutTree?.closeAndExpandIds(
            (pluck('id', session.sessionPath || []) || []) as number[]
          ),
        0
      )
      return true
    },
    [sessionsById] // eslint-disable-line
  )

  const SessionMarkers = React.useMemo(() => {
    if (isLoadDeviceCountInfo) {
      return []
    }
    const recrusiveForChildren = (session: any) => {
      if (session.children.length === 0) {
        return [session.id]
      }
      return session.children.map((child: any) => {
        return recrusiveForChildren(child)
      })
    }

    const children = pathOr([], ['children'], currentSession)

    return children.map((session: any) => {
      const allSessionIds = flatten(recrusiveForChildren(session))
      const statisticalByDeviceType = deviceCountInfoBySessionId

      const deviceCountInfo = flatten(
        allSessionIds.map((id: number) => path([id], statisticalByDeviceType))
      ) as IStatisticalByDeviceType[]

      const deviceType = filterVariables?.deviceType || DeviceType.CONNECTOR
      const statistical = sumStatisticalCountByDeviceType(
        deviceCountInfo,
        deviceType
      )
      const deviceCount = statistical.connectorCount
      const deviceChargingCount = statistical.summaryChargingCount
      const deviceUnavailableCount = statistical.summaryUnavailableCount
      const deviceAvailableCount = statistical.summaryAvailableCount

      if (isFilterActived) {
        if (deviceCount === 0) {
          return null
        }
        if (filterVariables?.available && deviceAvailableCount === 0) {
          return null
        }
        if (filterVariables?.charging && deviceChargingCount === 0) {
          return null
        }
        if (filterVariables?.unavailable && deviceUnavailableCount === 0) {
          return null
        }
      }
      return (
        <InnmaxMarker.Session
          key={session.id}
          session={session}
          deviceCountInfo={convertToDeviceCountInfoWithFilter(filterVariables, {
            deviceCount,
            deviceAvailableCount,
            deviceChargingCount,
            deviceUnavailableCount,
          })}
          statistical={deviceCountInfo}
          onLeftClick={(session: any) => {
            if (!checkIsTreeAndOpenLayerSidebar(session.id)) {
              closeMarkerSidebar()
              setCurrentSession(session)
            }
          }}
        />
      )
    })
  }, [
    currentSession,
    isLoadDeviceCountInfo,
    isFilterActived,
    filterVariables,
    deviceCountInfoBySessionId,
    getSessionDeepStatistical,
  ])

  React.useEffect(() => {
    if (!isStreetView) {
      return
    }
    closeMarkerSidebar()
  }, [isStreetView])

  const Markers = React.useMemo(() => {
    return <>{SessionMarkers}</>
  }, [SessionMarkers])

  useSetPageHeaderContent(<MapSettingDropdown lastUpdated={lastUpdated} />)

  const { getFullSessions } = useAllSessions()

  const openSideBar = (sideBar: 'filter' | 'layer') => {
    if (sideBar === 'filter') {
      filterSideBarOpen()
      layerSidebarClose()
    } else {
      layerSidebarOpen()
      filterSideBarClose()
    }
    mapInstance?.setFocusParkingLot(undefined)
  }
  const focusParkingLot = useWatchMap(mapInstance, 'focusParkingLot')
  const focusDevice = useWatchMap(mapInstance, 'focusDevice')

  React.useEffect(() => {
    if (focusDevice || focusParkingLot) {
      filterSideBarClose()
      layerSidebarClose()
    }
  }, [focusDevice, focusParkingLot])

  const renderLoading = () => {
    if (isLoadDeviceCountInfo || loadingDevices || parkingLotViewLoading) {
      return <LoadingBar />
    }
    return null
  }

  const renderMapView = () => {
    if (!currentSession || !baseLayer) {
      return (
        <div>
          <Loading />
        </div>
      )
    }
    if (viewMode === 'map') {
      return (
        <InnmaxMap2
          mapInstance={mapInstance}
          mapType={currentSession?.type}
          layerFileUrl={currentSession?.layerFileUrl}
          mapContainerStyle={{
            height: 'calc(100vh - 64px)',
          }}
          defaultCenter={{
            lat: currentSession.lat,
            lng: currentSession.lon,
          }}
          onRectangleComplete={rectangle => {
            const selectedDevices = sessionDevices?.filter(x =>
              rectangle
                .getBounds()
                ?.contains({ lat: x.lat as number, lng: x.lon as number })
            )
            mapInstance.setSelectedDevices(selectedDevices)
            mapInstance.setFocusDevice(path([0], selectedDevices))
          }}
          defaultZoom={8}
          onStreetViewChanged={setIsStreetView}
          googleMapOptions={{
            controlsOption: {
              zoomControlOptions: null,
              satelliteControlOption: undefined,
              streetViewControlOptions: null,
            },
          }}>
          {Markers}
        </InnmaxMap2>
      )
    }
    return <ParkingLotsView />
  }

  return (
    <Layout id="page-content" style={{ padding: 0 }}>
      <MapLayout>
        {!isStreetView && currentSession ? (
          <Breadcrumb
            layers={getFullSessions(currentSession.id as any) || []}
            onClick={e => {
              if (!checkIsTreeAndOpenLayerSidebar(e.id)) {
                setCurrentSession(sessionsById[e.id])
              }
            }}
          />
        ) : null}
        {isStreetView ? null : (
          <SimpleDeviceBoard
            onSelect={closeMarkerSidebar}
            lastUpdated={lastUpdated}
          />
        )}
        {renderLoading()}
        {/* <MarkerSidebar /> */}
        <ParkingLotSidebar />
        {Object.keys(videoPlayer).map((id: string) => (
          <VideoPlayer
            key={id}
            isOpen
            onRequestClose={() => closeVideoPlayer(videoPlayer[id].device)}
            device={videoPlayer[id].device}
          />
        ))}
        <FiltersSidebar
          visible={filterSideBarVisible}
          close={filterSideBarClose}
        />
        <LayerSidebar
          visible={layerSidebarVisible}
          close={layerSidebarClose}
          isLoading={false}
          layoutTree={layoutTree}
        />
        <AlarmList />
        <ChargingPointSidebar />

        <Controllers
          onFilterControlClick={() => openSideBar('filter')}
          onLayerControlClick={() => openSideBar('layer')}
          activedFilter={isFilterActived}
          avaliableControllers={viewMode === 'map' ? ['zoom'] : []}
        />
      </MapLayout>
      <div
        css={css`
          position: absolute;
          z-index: 1;
          top: 0;
          left: 0;
          height: calc(100vh - 64px);
          width: calc(100vw - 160px - 24px);
        `}>
        {isNil(currentSession) || isEmpty(currentSession)
          ? null
          : renderMapView()}
      </div>
    </Layout>
  )
}

const MapDashboard = () => {
  return (
    <MapDashboardProvider>
      <InternalMapDashboard />
    </MapDashboardProvider>
  )
}

export default MapDashboard

export function convertToDeviceCountInfoWithFilter(
  filter?: Record<string, any>,
  defaultInfo?: ISessionMarkerProps['deviceCountInfo']
): ISessionMarkerProps['deviceCountInfo'] {
  if (filter?.available) {
    return {
      deviceCount: defaultInfo?.deviceAvailableCount || 0,
      deviceUnavailableCount: 0,
      deviceAvailableCount: defaultInfo?.deviceAvailableCount || 0,
      deviceChargingCount: 0,
    }
  }
  if (filter?.charging) {
    return {
      deviceCount: defaultInfo?.deviceChargingCount || 0,
      deviceUnavailableCount: 0,
      deviceAvailableCount: 0,
      deviceChargingCount: defaultInfo?.deviceChargingCount || 0,
    }
  }
  if (filter?.unavailable) {
    return {
      deviceCount: defaultInfo?.deviceUnavailableCount || 0,
      deviceUnavailableCount: defaultInfo?.deviceUnavailableCount || 0,
      deviceAvailableCount: 0,
      deviceChargingCount: 0,
    }
  }

  return defaultInfo
}

const LoadingBar = () => {
  return (
    <div
      style={{
        position: 'absolute',
        top: '35px',
        left: 0,
        width: '100%',
        height: '3px',
        backgroundColor: '#ccc',
        zIndex: 1000,
      }}>
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 20%;
          height: 100%;
          background-color: ${theme.n.blue500};
          animation: ${LoadingAnimation} 1s linear infinite;
        `}
      />
    </div>
  )
}

const LoadingAnimation = keyframes`
   0% {
      left: 0;
  }
  100% {
      left:100%;
  }
`

const MapLayout = styled.div`
  position: absolute;
  width: calc() ();
  display: grid;
  pointer-events: none;
  grid-template-columns: 1fr 110px auto auto;
  height: calc(100vh - 64px);
  width: calc(100vw - 160px - 24px);
  left: 0px;
  top: 0px;
  grid-template-rows: 1fr auto;
  background-color: transparent;
  z-index: 2;
  grid-template-areas:
    'map controllers secondary-sidebar main-sidebar'
    'alarm-list alarm-list alarm-list main-sidebar'; ;
`
